'use client';

import { ChangeEvent, MouseEvent, useState } from 'react';
import { omit, round } from 'lodash';

import { UPLOAD_SUCCESS_MESSAGE } from '@/constants';
import { useFeaturedFlag, useNotify, useSelectedStore } from '@/hooks';
import {
  allowedAge,
  formatEnUSPhoneNumber,
  getCrmListIds,
  scrollToFirstInvalidField,
  scrollToTop,
} from '@/utils';
import {
  useAnalytics,
  useEcomStore,
  useForm,
  useGetAccount,
  useSetTreezCustomerUnverified,
  useSubscribeToCRM,
  useUpdateAccount,
  useValidator,
} from '@/data';
import {
  AccountPhoto,
  AddressMap,
  Alert,
  AuthContainer,
  Button,
  DateInput,
  Heading,
  IconSvg,
  Input,
  Modal,
  PhoneNumberInput,
  Spinner,
  toE164Phone,
  useAuth,
  useData,
} from '@/components';
import type { EnumCustomerType, OrderAddress, Store } from '@/types';
import {
  AccountType,
  EnumCustomerType as CustomerType,
  ProviderStoreType,
} from '@/types';

import {
  clearAccountAddress,
  formatDate,
  getAccountValidationRules,
  getMedicalIdPairErrors,
  getPasswordValidationRules,
  isMedicalIdPairInvalid,
  mapUpdateAccountInput,
} from './utils';

const AccountInfo = () => {
  const [state, setState] = useEcomStore();
  const { store } = useSelectedStore();
  const { allowIdVerifiationDelivery, allowIdVerifiationPickup } =
    useFeaturedFlag();

  const { notify } = useNotify();
  const { crm } = useData();
  const { isAuth, email, changePassword, signOut, updateUserAttributes, user } =
    useAuth();

  const { updateEndpoint } = useAnalytics();
  const [updated, setUpdated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isAddressOpen, setIsAddressOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [feedback, setFeedback] = useState<{
    error?: boolean;
    isOpen?: boolean;
    message?: string;
  }>({});

  const { inputs, setInputs, genericChange, inputChange, dateChange } =
    useForm();
  const { inputs: passwordInputs, inputChange: passwordInputChange } =
    useForm();

  const { loading: loadingData, data: accountInfo } = useGetAccount({
    onCompleted: res => {
      const medicalNumber = res.getAccountByEmail?.medical_id?.number ?? '';
      const medicalExpiration = formatDate(
        res.getAccountByEmail?.medical_id?.expiration_date,
      );
      setInputs({
        ...(omit(res.getAccountByEmail, [
          'accept_marketing',
          'medical_id',
          'medical_document_photo_uri',
          'phone',
        ]) ?? {}),
        accept_marketing: state?.buyer_accepts_marketing,
        phone: formatEnUSPhoneNumber(res.getAccountByEmail?.phone ?? ''),
        date_of_birth: formatDate(res.getAccountByEmail?.date_of_birth),
        driver_license_id_expiration_date: formatDate(
          res.getAccountByEmail?.driver_license_id_expiration_date,
        ),
        medical_id: medicalNumber,
        medical_id_expiration_date: medicalExpiration,
        medical_id_photo:
          res.getAccountByEmail?.medical_document_photo_uri ?? '',
      });
      setState({
        medical_id: medicalNumber,
        medical_id_expiration_date:
          res.getAccountByEmail?.medical_id?.expiration_date ?? '',
      });
    },
    skip: !isAuth || updated,
    fetchPolicy: 'network-only',
    // Update inputs with current server information.
    variables: {
      email,
      ...(store?.short_name
        ? {
            provider_store: {
              id: store?.short_name,
              provider: ProviderStoreType.Treez,
            },
          }
        : {}),
    },
  });

  const customerType =
    user?.attributes?.['custom:ACCOUNT_TYPE'] === AccountType.Medical
      ? CustomerType.MEDICAL
      : CustomerType.ADULT;

  const isMedicalCustomer =
    state?.customer_type !== CustomerType.ADULT || // this is set by med/rec toggle
    user?.attributes?.['custom:ACCOUNT_TYPE'] === AccountType.Medical;
  const showMedicalIdentification =
    isMedicalCustomer && store?.customer_type_allow !== CustomerType.ADULT;

  const minAge = allowedAge(
    (accountInfo?.type || customerType) as EnumCustomerType,
    store?.age_restriction,
  );

  const { validator, setIsSubmited, isSubmited, isInvalid } = useValidator(
    getAccountValidationRules(minAge),
    inputs,
  );

  const isMedicalPairInvalid =
    showMedicalIdentification && isMedicalIdPairInvalid(inputs);

  const {
    validator: passwordValidator,
    setIsSubmited: setIsPasswordSubmited,
    isInvalid: isPasswordInvalid,
  } = useValidator(getPasswordValidationRules(passwordInputs), passwordInputs);

  const { updateAccount } = useUpdateAccount();
  const { setTreezCustomerUnverified } = useSetTreezCustomerUnverified();

  const { subscribeToCRM } = useSubscribeToCRM({
    onCompleted: () => {
      updateUserAttributes({
        'custom:ACCEPT_MARKETING': (!!inputs.accept_marketing).toString(),
      }).then(() => {
        setState({
          buyer_accepts_marketing: !!inputs.accept_marketing,
        });
      });

      notify(
        'success',
        inputs?.accept_marketing
          ? 'Thanks for subscribing!'
          : 'You successfully unsubscribe',
      );
    },
    onError: error => {
      notify('error', `error: ${error}`);
    },
  });

  const handleAcceptMarketing = (e: ChangeEvent<HTMLInputElement>) => {
    setInputs({
      ...inputs,
      accept_marketing: e.target.checked,
    });

    const crmListIds = getCrmListIds(crm);

    subscribeToCRM({
      variables: {
        input: {
          email: inputs?.email,
          lists: crmListIds,
          source: 'ACCOUNT',
          unsubscribe: inputs?.accept_marketing,
        },
      },
    });
  };

  const showError = error => {
    setFeedback({
      error: true,
      isOpen: true,
      message:
        error.message ?? 'Account information not updated. Please, try again.',
    });
    scrollToTop();
    // eslint-disable-next-line no-console
    console.error(error);
  };

  const handleResetPassword = e => {
    e.preventDefault();

    setIsPasswordSubmited(true);
    setIsSubmited(false);
    setFeedback({});

    if (isPasswordInvalid) {
      scrollToTop();
      return;
    }

    setIsResetting(true);

    const oldPassword = passwordInputs.oldpassword;
    const newPassword = passwordInputs.password;

    changePassword(email, oldPassword, newPassword)
      .then(() => {
        setIsChangePasswordModalOpen(false);
        setFeedback({
          isOpen: true,
          message: 'Password was reset successfully.',
        });
      })
      .catch(e => showError(e))
      .finally(() => setIsResetting(false));
  };

  // eslint-disable-next-line no-unused-vars
  const handleUpdate = e => {
    e.preventDefault();

    setIsSubmited(true);
    setIsPasswordSubmited(false);
    setFeedback({});

    if (isInvalid || isMedicalPairInvalid) {
      // Defer so the just-submitted invalid state (aria-invalid) is rendered,
      // then scroll to the first failing field instead of jumping to the top.
      setTimeout(() => {
        if (!scrollToFirstInvalidField()) {
          scrollToTop();
        }
      }, 0);
      return;
    }

    const phoneNumber = toE164Phone(inputs?.phone as string) ?? undefined;

    setIsLoading(true);

    // Update the account in Cognito using predefined
    // attributes.
    updateUserAttributes({
      address: inputs.address1 ?? '',
      'custom:ACCEPT_MARKETING': (!!inputs.accept_marketing).toString(),
      'custom:CITY': inputs.city ?? '',
      'custom:COUNTRY_CODE': inputs.country_code ?? '',
      'custom:GC_CUSTOMER_ID': inputs.entity_id,
      'custom:PROVINCE_CODE': inputs.province_code ?? '',
      'custom:ZIP': inputs.zip ?? '',
      family_name: inputs.last_name,
      given_name: inputs.first_name,
      birthdate: inputs.date_of_birth,
      phone_number: phoneNumber,
    })
      .then(() => {
        setUpdated(true);

        // Update endpoint in Pinpoint.
        updateEndpoint({
          address: email,
          location: {
            city: inputs.city,
            country: inputs.country_code ?? 'US',
            latitude: round(inputs.latitude, 1),
            longitude: round(inputs.longitude, 1),
            postalCode: inputs.zip,
            region: inputs.province_code,
          },
          userAttributes: {
            firstName: [inputs.first_name],
            lastName: [inputs.last_name],
            specialOffersOptOut: [inputs.accept_marketing ? 'NONE' : 'ALL'],
          },
        });

        // Use address from card (state.addresses[0]) so it is sent to the backend.
        const inputsForUpdate =
          state.addresses?.length > 0
            ? { ...inputs, ...state.addresses[0] }
            : inputs;
        const input = mapUpdateAccountInput(inputsForUpdate);

        // Update the account in GapCommerce.
        return updateAccount({
          variables: {
            input,
          },
        });
      })
      .then(() => {
        // Update stored information (including address so checkout has it).
        const addressFields =
          state.addresses?.length > 0 ? state.addresses[0] : {};
        setState({
          ...state,
          medical_id: inputs.medical_id,
          medical_id_expiration_date: inputs.medical_id_expiration_date
            ? new Date(inputs.medical_id_expiration_date)?.toISOString()
            : '',
          delivery_address: {
            first_name: inputs.first_name,
            last_name: inputs.last_name,
            phone: phoneNumber,
            ...addressFields,
          },
        });

        setFeedback({
          isOpen: true,
          message: 'Account information updated successfully.',
        });
        scrollToTop();
      })
      .catch(e => showError(e))
      .finally(() => setIsLoading(false));
  };

  const handleImageChange = (field, url, error) => {
    if (error) {
      showError({ message: error });
      return;
    }

    setIsLoading(true);

    genericChange(field, url);

    const input = mapUpdateAccountInput({
      ...inputs,
      [field]: url,
    });

    updateAccount({
      variables: {
        input,
      },
    })
      .then(() => {
        setFeedback({
          isOpen: true,
          message: UPLOAD_SUCCESS_MESSAGE,
        });

        // eslint-disable-next-line no-console
        setTreezCustomerUnverified()?.catch(e => console.error(e));

        scrollToTop();
      })
      .catch(e => showError({ message: e }))
      .finally(() => setIsLoading(false));
  };

  const handleMedicalDocumentUpload = (field, url, error) => {
    if (error) {
      showError({ message: error });
      return;
    }
    setIsUploadingDocument(true);

    genericChange(field, url);

    const input = mapUpdateAccountInput({
      ...inputs,
      [field]: url,
    });

    updateAccount({
      variables: {
        input,
      },
    })
      .then(() => {
        setState({
          medical_id: inputs.medical_id ?? '',
          medical_id_expiration_date: inputs.medical_id_expiration_date
            ? new Date(inputs.medical_id_expiration_date).toISOString()
            : '',
        });
        setFeedback({
          isOpen: true,
          message: UPLOAD_SUCCESS_MESSAGE,
        });

        scrollToTop();
      })
      .catch(e => showError({ message: e }))
      .finally(() => setIsUploadingDocument(false));
  };

  if (loadingData) {
    return (
      <div className="account__auth-loading">
        <Spinner>Loading...</Spinner>
      </div>
    );
  }

  return (
    <AuthContainer>
      <div className="account__auth-container">
        <PersonalInformationSection
          dateChange={dateChange}
          email={email}
          feedback={feedback}
          inputChange={inputChange}
          inputs={inputs}
          onChangePasswordClick={() => setIsChangePasswordModalOpen(true)}
          onClearFeedback={() => setFeedback({})}
          validator={validator}
        />
        <ChangePasswordModal
          isOpen={isChangePasswordModalOpen}
          isResetting={isResetting}
          onClose={() => setIsChangePasswordModalOpen(false)}
          onSubmit={handleResetPassword}
          passwordInputChange={passwordInputChange}
          passwordInputs={passwordInputs}
          passwordValidator={passwordValidator}
        />
        <NotificationsSection
          acceptMarketing={inputs.accept_marketing}
          onAcceptMarketingChange={handleAcceptMarketing}
        />
        <PersonalIdentificationSection
          allowIdVerification={
            allowIdVerifiationPickup || allowIdVerifiationDelivery
          }
          dateChange={dateChange}
          inputChange={inputChange}
          inputs={inputs}
          isLoading={isLoading}
          onImageChange={handleImageChange}
          validator={validator}
        />
        {showMedicalIdentification && (
          <MedicalIdentificationSection
            dateChange={dateChange}
            inputChange={inputChange}
            inputs={inputs}
            isSubmited={isSubmited}
            isUploadingDocument={isUploadingDocument}
            onMedicalDocumentUpload={handleMedicalDocumentUpload}
            validator={validator}
          />
        )}
        <AddressSection
          inputs={inputs}
          isAddressOpen={isAddressOpen}
          isResetting={isResetting}
          onClearAddress={() =>
            clearAccountAddress({
              inputs,
              setInputs,
              setState,
              state,
            })
          }
          setInputs={setInputs}
          setIsAddressOpen={setIsAddressOpen}
          setState={setState}
          state={state}
        />
        <UpdateAccountFooter
          isLoading={isLoading}
          onSignOut={signOut}
          onUpdate={handleUpdate}
        />
      </div>
    </AuthContainer>
  );
};

function PersonalInformationSection({
  dateChange,
  email,
  feedback,
  inputChange,
  inputs,
  onClearFeedback,
  onChangePasswordClick,
  validator,
}: {
  dateChange: (_e: ChangeEvent<HTMLInputElement>) => void;
  email: string | undefined;
  feedback: { error?: boolean; isOpen?: boolean; message?: string };
  inputChange: (_e: ChangeEvent<HTMLInputElement>) => void;
  inputs: Record<string, unknown>;
  onClearFeedback: () => void;
  onChangePasswordClick: () => void;
  validator: Record<string, unknown>;
}) {
  const v = validator as Record<
    string,
    { message?: string; isInvalid?: boolean }
  >;
  return (
    <section>
      <Heading className="account__auth_title" level={2}>
        Personal information
      </Heading>
      <Alert
        error={!!feedback.error}
        isOpen={!!feedback.isOpen}
        toggle={onClearFeedback}
      >
        {feedback.message}
      </Alert>
      <form className="account__body">
        <div className="account__form-fieldset">
          <div className="account__form-body">
            <Input
              feedback={v.first_name?.message}
              inputLabel="First Name"
              isInvalid={v.first_name?.isInvalid}
              name="first_name"
              onChange={inputChange}
              placeholder="First name"
              value={(inputs.first_name as string) ?? ''}
            />
            <Input
              feedback={v.last_name?.message}
              inputLabel="Last Name"
              isInvalid={v.last_name?.isInvalid}
              name="last_name"
              onChange={inputChange}
              placeholder="Last Name"
              value={(inputs.last_name as string) ?? ''}
            />
            <DateInput
              autoComplete="off"
              feedback={v.date_of_birth?.message}
              inputLabel="Date of birth"
              isInvalid={v.date_of_birth?.isInvalid}
              name="date_of_birth"
              onChange={dateChange}
              placeholder="Date of birth"
              showCalendarIcon
              type="date"
              value={(inputs.date_of_birth as string) ?? ''}
            />
          </div>
        </div>
        <div className="account__form-fieldset">
          <div className="account__form-body">
            <Input
              disabled
              feedback={v.email?.message}
              inputLabel="Email"
              name="email"
              onChange={inputChange}
              placeholder="Email"
              type="email"
              value={email ?? ''}
            />
            <PhoneNumberInput
              feedback={v.phone?.message}
              inputLabel="Phone"
              isInvalid={v.phone?.isInvalid}
              name="phone"
              onChange={inputChange}
              placeholder="Phone number"
              value={(inputs.phone as string) ?? ''}
            />
            <Button
              ariaLabel="Change password"
              className="account__change-password-trigger"
              color="primary"
              onClick={onChangePasswordClick}
              type="button"
            >
              <div className="account__change-password-trigger__inner">
                <IconSvg name="change-password" />
                <span>Change password</span>
              </div>
            </Button>
          </div>
        </div>
      </form>
    </section>
  );
}

function MedicalIdentificationSection({
  inputs,
  validator,
  isSubmited,
  inputChange,
  dateChange,
  onMedicalDocumentUpload,
  isUploadingDocument,
}: {
  inputs: Record<string, unknown>;
  validator: Record<string, unknown>;
  isSubmited: boolean;
  inputChange: (_e: ChangeEvent<HTMLInputElement>) => void;
  dateChange: (_e: ChangeEvent<HTMLInputElement>) => void;
  onMedicalDocumentUpload: (
    _field: string,
    _url: string,
    _error: string | null,
  ) => void;
  isUploadingDocument: boolean;
}) {
  const v = validator as Record<
    string,
    { message?: string; isInvalid?: boolean }
  >;
  const pairErrors = isSubmited ? getMedicalIdPairErrors(inputs) : {};
  const medicalIdError = pairErrors.medical_id ?? v.medical_id;
  const medicalExpError =
    pairErrors.medical_id_expiration_date ?? v.medical_id_expiration_date;

  return (
    <div className="account__identification-container-photo">
      <Heading
        className="account__auth_title account__auth_title--mb-small"
        level={2}
      >
        Medical Identification
      </Heading>
      <p className="identification_description">
        Help us maintain a secure medical program. Provide your medical
        information and upload a valid medical ID.
      </p>
      <div className="account__form-legend">
        <div className="account__form-column">
          <Input
            feedback={medicalIdError?.message}
            inputLabel="Medical ID Number*"
            isInvalid={medicalIdError?.isInvalid}
            name="medical_id"
            onChange={inputChange}
            placeholder="Medical ID Number*"
            value={(inputs.medical_id as string) ?? ''}
          />
          <DateInput
            autoComplete="off"
            className="identification_expiration_date"
            feedback={medicalExpError?.message}
            inputLabel="Medical ID Expiration Date*"
            isInvalid={medicalExpError?.isInvalid}
            name="medical_id_expiration_date"
            onChange={dateChange}
            placeholder="Medical ID Expiration Date*"
            showCalendarIcon
            type="date"
            value={(inputs.medical_id_expiration_date as string) ?? ''}
          />
        </div>
        <AccountPhoto
          alt="Medical ID picture"
          field="medical_id_photo"
          loading={isUploadingDocument}
          onChange={onMedicalDocumentUpload}
          title="Your medical license or certification."
          value={(inputs.medical_id_photo as string) ?? ''}
        />
      </div>
    </div>
  );
}

function ChangePasswordModal({
  isOpen,
  isResetting,
  onClose,
  onSubmit,
  passwordInputChange,
  passwordInputs,
  passwordValidator,
}: {
  isOpen: boolean;
  isResetting: boolean;
  onClose: () => void;
  onSubmit: (_e: MouseEvent) => void;
  passwordInputChange: (_e: ChangeEvent<HTMLInputElement>) => void;
  passwordInputs: Record<string, unknown>;
  passwordValidator: Record<string, unknown>;
}) {
  const pv = passwordValidator as Record<
    string,
    { message?: string; isInvalid?: boolean }
  >;
  return (
    <Modal
      ariaLabel="Change password"
      className="account__change-password-modal"
      isOpen={isOpen}
      md
      toggle={onClose}
    >
      <Heading className="account__change-password-modal-title" level={4}>
        Change password
      </Heading>
      <div className="account__form-body">
        <Input
          autoComplete="old-password"
          feedback={pv.oldpassword?.message}
          inputLabel="Old password"
          isInvalid={pv.oldpassword?.isInvalid}
          isVisibilityControl
          name="oldpassword"
          onChange={passwordInputChange}
          placeholder="Old password"
          type="password"
          value={(passwordInputs.oldpassword as string) ?? ''}
        />
        <Input
          autoComplete="new-password"
          feedback={pv.password?.message}
          inputLabel="New password"
          isInvalid={pv.password?.isInvalid}
          isVisibilityControl
          name="password"
          onChange={passwordInputChange}
          placeholder="New password"
          type="password"
          value={(passwordInputs.password as string) ?? ''}
        />
        <Input
          autoComplete="new-password"
          feedback={pv.passwordmatch?.message}
          inputLabel="Confirm new password"
          isInvalid={pv.passwordmatch?.isInvalid}
          isVisibilityControl
          name="passwordmatch"
          onChange={passwordInputChange}
          placeholder="Confirm new password"
          type="password"
          value={(passwordInputs.passwordmatch as string) ?? ''}
        />
      </div>
      <div className="account__change-password-modal-actions">
        <Button ariaLabel="Cancel" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          ariaLabel="Save"
          color="primary"
          loading={isResetting}
          onClick={onSubmit}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
}

function NotificationsSection({
  acceptMarketing,
  onAcceptMarketingChange,
}: {
  acceptMarketing: boolean | undefined;
  onAcceptMarketingChange: (_e: ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="account__notifications">
      <Heading className="account__auth_title" level={2}>
        Notifications
      </Heading>
      <Input
        checked={acceptMarketing}
        id="accept_marketing"
        name="accept_marketing"
        onChange={onAcceptMarketingChange}
        type="checkbox"
        value={acceptMarketing}
      >
        <label htmlFor="accept_marketing">
          Receive email promotions and updates
        </label>
      </Input>
    </div>
  );
}

function PersonalIdentificationSection({
  inputs,
  validator,
  inputChange,
  dateChange,
  onImageChange,
  allowIdVerification,
  isLoading,
}: {
  inputs: Record<string, unknown>;
  validator: Record<string, unknown>;
  inputChange: (_e: ChangeEvent<HTMLInputElement>) => void;
  dateChange: (_e: ChangeEvent<HTMLInputElement>) => void;
  onImageChange: (_field: string, _url: string, _error: string | null) => void;
  allowIdVerification: boolean;
  isLoading: boolean;
}) {
  const v = validator as Record<
    string,
    { message?: string; isInvalid?: boolean }
  >;
  return (
    <div className="account__identification-container-photo">
      <Heading
        className="account__auth_title account__auth_title--mb-small"
        level={2}
      >
        Personal Identification
      </Heading>
      <p className="identification_description">
        Help us to increase our security. To check your ID complete the
        information below, upload a photo of your driver&apos;s license or
        government issued ID.
      </p>
      <div className="account__form-legend">
        <div className="account__form-column">
          <Input
            autoComplete="off"
            inputLabel="Driver license number"
            name="driver_license_id"
            onChange={inputChange}
            placeholder="Driver license number"
            value={(inputs.driver_license_id as string) ?? ''}
          />
          <DateInput
            autoComplete="off"
            className="identification_expiration_date"
            feedback={v.driver_license_id_expiration_date?.message}
            inputLabel="DL expiration date"
            isInvalid={v.driver_license_id_expiration_date?.isInvalid}
            name="driver_license_id_expiration_date"
            onChange={dateChange}
            placeholder="DL expiration date"
            showCalendarIcon
            type="date"
            value={(inputs.driver_license_id_expiration_date as string) ?? ''}
          />
        </div>
        {allowIdVerification && (
          <AccountPhoto
            alt="Driver licence picture"
            field="driver_license_photo"
            loading={isLoading}
            onChange={onImageChange}
            title="Upload a photo or PDF of your drivers license or government issued ID."
            value={(inputs.driver_license_photo as string) ?? ''}
          />
        )}
      </div>
    </div>
  );
}

function AddressSection({
  state,
  setState,
  inputs,
  setInputs,
  isAddressOpen,
  setIsAddressOpen,
  onClearAddress,
  isResetting,
}: {
  state: unknown;
  setState: (_patch: Record<string, unknown>) => void;
  inputs: Record<string, unknown>;
  setInputs: (_value: Record<string, unknown>) => void;
  isAddressOpen: boolean;
  setIsAddressOpen: (_open: boolean) => void;
  onClearAddress: () => void;
  isResetting: boolean;
}) {
  const s = state as { addresses?: Array<Record<string, unknown>> };
  const addresses = s.addresses ?? [];
  const hasAddress = addresses.length > 0;
  const address0 = hasAddress ? addresses[0] : null;

  return (
    <div className="account__address">
      <Heading className="account__auth_title" level={2}>
        Your Address
      </Heading>

      {hasAddress && address0 && (
        <div className="account__address-card">
          <div className="account__address-card__body">
            <div className="account__address-card__line1">
              {String(address0.address1 ?? '')}
              {address0.address2 ? ` ${address0.address2}` : ''}
            </div>
            <div className="account__address-card__line2">
              {[
                address0.city,
                address0.province,
                address0.zip,
                address0.country_code || address0.country,
              ]
                .filter(Boolean)
                .join(', ')}
            </div>
          </div>
          <div className="account__address-card__actions">
            <button
              aria-label="Edit address"
              className="account__address-card__action"
              onClick={() => setIsAddressOpen(true)}
              type="button"
            >
              <IconSvg name="edit" />
            </button>
            <button
              aria-label="Remove address"
              className="account__address-card__action"
              onClick={onClearAddress}
              type="button"
            >
              <IconSvg name="delete" />
            </button>
          </div>
        </div>
      )}

      {!hasAddress && (
        <Button
          ariaLabel="Add an address"
          className="account__add-address-btn"
          color="secondary"
          loading={isResetting}
          onClick={() => setIsAddressOpen(true)}
        >
          <div className="account__add-address-btn__content">
            <IconSvg className="account__add-address-icon" name="plus" />
            <span>ADD ADDRESS</span>
          </div>
        </Button>
      )}

      <Modal
        className="account__address_modal"
        isOpen={isAddressOpen}
        md
        toggle={() => setIsAddressOpen(false)}
      >
        <AddressMap
          editable
          id="address1"
          isAccount
          noType
          onChange={(address, addressesList) => {
            setState({ addresses: addressesList });
            setInputs({
              ...inputs,
              ...address,
            });
          }}
          setIsAddressOpen={() => setIsAddressOpen(false)}
          setState={setState}
          state={(s.addresses ?? []) as OrderAddress[] | Store[]}
          value={inputs}
        />
      </Modal>
    </div>
  );
}

function UpdateAccountFooter({
  isLoading,
  onSignOut,
  onUpdate,
}: {
  isLoading: boolean;
  onSignOut: () => void;
  onUpdate: (_e: MouseEvent) => void;
}) {
  return (
    <div className="account__update-footer">
      <Button
        ariaLabel="Update account"
        className="account__update-btn account__footer-btn"
        color="primary"
        loading={isLoading}
        onClick={onUpdate}
      >
        Update account
      </Button>
      <Button
        ariaLabel="Sign out"
        className="account__signout-btn account__footer-btn account__footer-btn--signout"
        color="secondary"
        onClick={() => onSignOut()}
      >
        <span className="account__signout-btn__text">Sign out</span>
      </Button>
    </div>
  );
}

export { AccountInfo };
